/* eslint-disable jira/filename-case */

import React, { useState, useEffect, useCallback } from 'react';

import { withRouter } from '@atlassian/react-resource-router';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	fetchApplink,
	fetchApplinkStatus,
	getOAuth2ConfigInfo,
} from './fetchApplinkManifestForUrl.tsx';
import type { ApplicationLinkV3Status } from './CommonApplicationLinkCreationEntity.tsx';
import ApplinksEditPageContents, { type ApplinksProps } from './ApplinksEditPageContents.tsx';

export const ApplinksPage = (props: { id: string | null }): React.JSX.Element => {
	const [state, setState] = useState<{
		applinkStatus: ApplicationLinkV3Status | null;
		applinkEditPageData: ApplinksProps | null;
		isLoading: boolean;
	}>({
		applinkStatus: null,
		applinkEditPageData: null,
		isLoading: true,
	});

	const doRefreshStatus = useCallback(() => {
		setState((prev) => ({ ...prev, applinkStatus: null }));

		if (props.id) {
			fetchApplinkStatus(props.id)
				.then((status) => {
					setState((prev) => ({ ...prev, applinkStatus: status }));
				})
				.catch((err) => {
					const maybeError = err instanceof Error ? err : undefined;
					log.safeErrorWithoutCustomerData(
						'applinks.administration.edit.refreshStatus',
						'Failed to refresh applink status when requested by the edit form.',
						maybeError,
					);
				});
		}
	}, [props.id]);

	useEffect(() => {
		const loadAllData = async () => {
			if (props.id === null) {
				log.safeErrorWithoutCustomerData(
					'applinks.administration.edit.loadAllData',
					'Edit form was loaded but no applink id was provided',
				);
				setState((prev) => ({ ...prev, applinkEditPageData: null, isLoading: false }));
				return;
			}

			setState((prev) => ({ ...prev, isLoading: true }));

			try {
				// Load applink data and status in parallel
				const [applinkData, status] = await Promise.all([
					fetchApplink(props.id),
					fetchApplinkStatus(props.id).catch((err) => {
						const maybeError = err instanceof Error ? err : undefined;
						log.safeErrorWithoutCustomerData(
							'applinks.administration.edit.loadAllData',
							'Failed to fetch applink status (as part of initial form load)',
							maybeError,
						);

						return null;
					}),
				]);

				let pageData: ApplinksProps | null = null;

				if (applinkData.system) {
					pageData = {
						kind: 'ERROR',
						err: 'NOT_EDITABLE',
					};
				} else if (applinkData.data.isOAuth2) {
					try {
						const configInfo = await getOAuth2ConfigInfo(applinkData.id);
						pageData = { kind: 'OAUTH2', applink: applinkData, oauth2ConfigInfo: configInfo };
					} catch (err) {
						const maybeError = err instanceof Error ? err : undefined;
						log.safeErrorWithoutCustomerData(
							'applinks.administration.edit.loadAllData',
							'Failed to fetch OAuth 2.0 config info (as part of initial form load)',
							maybeError,
						);
						pageData = { kind: 'OAUTH2', applink: applinkData, oauth2ConfigInfo: null };
					}
				} else {
					pageData = { kind: 'OAUTH1', applink: applinkData };
				}

				setState({ applinkStatus: status, applinkEditPageData: pageData, isLoading: false });
			} catch (err) {
				const maybeError = err instanceof Error ? err : undefined;
				log.safeErrorWithoutCustomerData(
					'applinks.administration.edit.loadAllData',
					'Failed to fetch applink data with miscellaneous error (as part of initial form load)',
					maybeError,
				);
				setState((prev) => ({ ...prev, applinkEditPageData: null, isLoading: false }));
			}
		};

		loadAllData();
	}, [props.id]);

	return (
		<ApplinksEditPageContents
			isLoading={state.isLoading}
			data={state.applinkEditPageData}
			status={state.applinkStatus}
			onRequestStatusRefresh={doRefreshStatus}
		/>
	);
};

const Edit: {
	(props: { id: string | null }): React.JSX.Element;
	displayName: string;
} = withRouter(ApplinksPage);

export default Edit;
